import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ParcelCreateFormValues } from '../../../hooks/useParcelCreateFormSchema';
import { useParcelFormError } from '../../../hooks/useParcelFormError';
import { deriveRecurrencePreview } from '../../../utils/deriveRecurrencePreview';
import { ParcelNumericField } from '../../molecules/ParcelNumericField';
import { PlanReadOnlyField } from '../../molecules/PlanReadOnlyField';
import { PlanToggleBlock } from '../../molecules/PlanToggleBlock';

/** Props de `ParcelRecurrenceBlock`. */
export interface ParcelRecurrenceBlockProps {
  readonly form: UseFormReturn<ParcelCreateFormValues>;
}

/**
 * Bloco de recorrência da criação de parcela.
 *
 * O legado tem apenas um "Sim"; aqui o interruptor revela a **quantidade**, porque
 * `multiple: true` sem `amountOfParcel` não diz ao backend quantas cópias criar.
 *
 * A projeção é da tela, não do backend: não existe endpoint de pré-checagem de
 * número de parcela, então ela serve para o operador conferir a intenção — o
 * backend segue podendo recusar número já usado no mesmo tipo. O texto de apoio
 * diz isso em vez de deixar a projeção passar por confirmação.
 *
 * Desligar o interruptor desmonta a quantidade, para não deixar campo inválido
 * invisível bloqueando o envio.
 */
export function ParcelRecurrenceBlock({ form }: ParcelRecurrenceBlockProps) {
  const { t } = useTranslation('individualPayment');
  const { control, watch, setValue } = form;
  const errorOf = useParcelFormError(form);

  const isRecurrent = watch('multiple') === true;
  const preview = deriveRecurrencePreview(
    watch('numberParcel') as number | null | undefined,
    watch('expirationDate') as string | null | undefined,
    watch('amountOfParcel') as number | null | undefined,
  );

  return (
    <PlanToggleBlock
      title={t('parcelCreate.recurrence.title')}
      hint={t('parcelCreate.recurrence.hint')}
      // Seção de primeiro nível, ao lado dos outros cards — branco, não o cinza de
      // bloco aninhado que a edição de plano usa.
      tone="surface"
      checked={isRecurrent}
      onCheckedChange={checked => {
        setValue('multiple', checked, { shouldDirty: true });
        if (!checked) setValue('amountOfParcel', undefined, { shouldDirty: true });
      }}
    >
      <div className="flex flex-wrap gap-xxxs">
        <div className="flex flex-1 basis-64">
          <ParcelNumericField
            control={control}
            name="amountOfParcel"
            kind="integer"
            required
            label={t('parcelCreate.recurrence.amount')}
            placeholder={t('parcelCreate.recurrence.amountPlaceholder')}
            error={errorOf('amountOfParcel')}
          />
        </div>
        <div className="flex flex-1 basis-64">
          <PlanReadOnlyField
            label={t('parcelCreate.recurrence.previewLabel')}
            value={
              preview === null
                ? t('parcelCreate.recurrence.previewEmpty')
                : t('parcelCreate.recurrence.preview', {
                    from: preview.from,
                    to: preview.to,
                    firstDate: preview.firstDate,
                    lastDate: preview.lastDate,
                  })
            }
            hint={t('parcelCreate.recurrence.previewHint')}
          />
        </div>
      </div>
    </PlanToggleBlock>
  );
}
